import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Radio,
  RadioGroup,
  VStack,
  Text,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { getBackendURL } from "./env";
import {
  fetchChatModels,
  chatModelOptions,
  resolveChatModelId,
} from "../utils/chatModels";

// Picks the Claude model used for form generation. The list is the newest
// models fetched live from Anthropic (via /api/ai-assistant/models/), so a new
// release shows up here without a code change. `currentModel` may be a legacy
// numeric choice (0/1/2) or a model id; onSave always receives a model id.
const LLMModelSelectionModal = ({ isOpen, onClose, currentModel, onSave }) => {
  const [catalog, setCatalog] = useState({
    chatModels: [],
    legacyMap: null,
    loading: false,
    error: null,
  });
  const [selectedModel, setSelectedModel] = useState(() =>
    resolveChatModelId(currentModel),
  );
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setCatalog((c) => ({ ...c, loading: true }));
    fetchChatModels(getBackendURL()).then((r) => {
      if (cancelled) return;
      setCatalog({
        chatModels: r.chatModels,
        legacyMap: r.legacyMap,
        loading: false,
        error: r.ok ? null : r.error,
      });
      setSelectedModel(resolveChatModelId(currentModel, r.legacyMap));
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentModel]);

  const { options } = chatModelOptions(
    catalog.chatModels,
    selectedModel,
    catalog.legacyMap,
  );

  const handleSave = () => {
    onSave(selectedModel);
    toast({
      title: "Model Updated",
      description: `LLM model changed to ${
        options.find((o) => o.value === selectedModel)?.label || selectedModel
      }`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Select LLM Model</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="start">
            <Text>
              Choose the AI model to use for generating forms and analyzing
              patient data.
            </Text>
            {catalog.loading && options.length === 0 ? (
              <Spinner size="sm" />
            ) : (
              <RadioGroup
                value={selectedModel}
                onChange={(value) => setSelectedModel(value)}
              >
                <VStack spacing={3} align="start">
                  {options.map((o) => (
                    <Radio key={o.value} value={o.value}>
                      <VStack spacing={1} align="start">
                        <Text fontWeight="bold">{o.label}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {o.value}
                        </Text>
                      </VStack>
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
            )}
            <Text fontSize="xs" color="gray.500">
              {catalog.error
                ? `Live model list unavailable (${catalog.error}); showing the last known models.`
                : "Newest Claude models, fetched live from Anthropic."}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSave}>
            Save
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LLMModelSelectionModal;
